import React, { FC, MutableRefObject, ReactElement, useEffect, useMemo, useRef } from 'react'
import { CRMConfigurationEntry, CRMFormData, CRMServerResponse } from '../Types/Types'
import { useForm, UseFormMethods } from '../../../../../../Vendor/react-hook-form/dist/index.ie11'
import { CRMModalID } from '../Constants/Constants'
import { CRMContent } from './CRMContent'
import { ON_CHANGE } from '../../../../../../React/Common/Constants/HookFormEventNames'
import { EMPTY_STRING } from '../../../../../../React/Common/Constants/Separators'
import { ModalUtilsProvider } from '../../../../../../React/Common/Contexts/ModalUtilsContext'
import { DEFAULT_CME_ID } from '../../../../../../React/Common/Constants/ServerValues'
import {
    ALL_VALUE,
    ANY_VALUE_STRING_DEFAULT,
    ANY_VALUE_STRING_NUMERIC
} from '../../../../../../React/Common/Constants/DefaultValues'
import { isVarNotEmpty } from '../../../../../../NonReact/utils/GlobalFunctions'
import { mapFromServerToForm } from '../Mappers/Mappers'
import { AdminFilterSingleValues } from '../../../../Molecules/AdminFilters/AdminFilters'
import { DEFAULT_PASSWORD_VALUE } from '../../../../../../NonReact/constants/Common'
import { CRM_OPTIONS_SCOPE_NAME } from '../Constants/FieldNames'

type Props = {
    filters: Nullable<AdminFilterSingleValues>
    currentCRMConfiguration?: Nullable<CRMServerResponse>
    closeModal: (refresh: boolean) => void
    isViewOnly: boolean
}

export const CRM: FC<Props> = ({ currentCRMConfiguration, filters, closeModal, isViewOnly }: Props): ReactElement => {
    // Sets the default values for the form within the Modal.
    const defaultValues: MutableRefObject<CRMFormData> = useRef({
        details: {
            description: EMPTY_STRING,
            ID: EMPTY_STRING
        },
        scopeAssignment: {
            cme: filters?.cme?.toString() ?? DEFAULT_CME_ID,
            telephonyServer: filters?.telephonyServer ?? ALL_VALUE,
            partition: filters?.partition ?? ALL_VALUE,
            groupDevice: filters?.device ?? ANY_VALUE_STRING_DEFAULT,
            filterRecursively: false
        },
        configuration: {
            crmType: ANY_VALUE_STRING_NUMERIC,
            crmOptions: []
        }
    })

    // Form context.
    const methods: UseFormMethods<CRMFormData> = useForm<CRMFormData>({
        defaultValues: defaultValues.current,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    const { reset, setValue }: UseFormMethods<CRMFormData> = methods

    // get all properties from defaultValues
    const validationFields: string[] = useMemo(
        (): string[] => Object.keys(defaultValues.current),
        [JSON.stringify(defaultValues.current)]
    )

    useEffect((): void => {
        if (isVarNotEmpty(currentCRMConfiguration)) {
            const mappedConfiguration: CRMFormData = mapFromServerToForm(currentCRMConfiguration)
            reset(mappedConfiguration)
            setTimeout((): void => {
                mappedConfiguration.configuration.crmOptions.forEach(
                    (item: CRMConfigurationEntry, index: number): void => {
                        if (item.value === DEFAULT_PASSWORD_VALUE) {
                            setValue(`${CRM_OPTIONS_SCOPE_NAME}[${index}].value`, DEFAULT_PASSWORD_VALUE)
                        }
                    }
                )
            }, 500)
        }
    }, [currentCRMConfiguration])

    return (
        <ModalUtilsProvider
            initialTabID={CRMModalID.CRMModalDetails}
            validationFields={validationFields}
            closeModal={closeModal}
            methods={methods as unknown as UseFormMethods}
        >
            <CRMContent
                currentCRMConfiguration={currentCRMConfiguration}
                closeModal={closeModal}
                methods={methods}
                viewOnly={isViewOnly}
            />
        </ModalUtilsProvider>
    )
}
